import useAlchemystStoreForAi from '@/hooks/ai/client/useAlchemystStoreForAi';
import { useSession } from '@/hooks/useSession';
import { AlchemystProps } from '@/types/appStates';
import {
  InitialSuggestedQuestionsProps,
  Question,
} from '@/types/components/converse/messages';
import { Avatar } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MarkdownRender from './MarkdownRenderer';

const questions: Record<AlchemystProps['persona'], Question[]> = {
  maya: [
    {
      id: 1,
      question:
        'Can you please summarize the contents of: [**Wikipedia Top 25**](https://en.wikipedia.org/wiki/Wikipedia:Top_25_Report) ?',
      icon: '💼',
    },
    {
      id: 2,
      question: 'Who are the top tech investors in London?',
      icon: '🏙️',
      tools: {
        webSearch: true,
      },
    },
    {
      id: 3,
      question: 'Can you tell me who are the latest investors in OpenAI?',
      icon: '🚀',
      tools: {
        webSearch: true,
      },
    },
    {
      id: 4,
      question: 'Help me research about my ICPs',
      icon: '💳',
      tools: {
        researchIcps: true,
      },
    },
  ],
  ron: [
    {
      id: 1,
      question: 'Can you give me an itinerary for a 3-day trip to Paris?',
      icon: '💼',
      tools: {
        deepResearch: true,
      },
    },
    {
      id: 2,
      question: 'What stocks should i buy in the next 10-15 days?',
      icon: '📈',
      tools: {
        deepResearch: true,
      },
    },
    {
      id: 3,
      question:
        'Can you tell me what type of content does Gen Z consume the most?',
      icon: '🚀',
      tools: {
        deepResearch: true,
      },
    },
    {
      id: 4,
      question: 'Help me make a marketing campaign for my B2B product',
      icon: '🗣',
      tools: {
        organizationSearch: true,
        deepResearch: true,
      },
    },
  ],
  moh: [],
};

const getWish = (): string => {
  const hour = new Date().getHours();
  if (hour < 5) return 'Late night';
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  if (hour < 22) return 'Good evening';
  return 'Late night';
};

/**
 * InitialSuggestedQuestions component
 *
 * Displays a set of suggested questions to help users get started with the chatbot.
 * The component provides a visual guide to the user, suggesting common questions
 * that can be asked to the chatbot.
 *
 * @param onSend - Callback to send a message to the chatbot
 */

function InitialSuggestedQuestions({
  onSend,
}: InitialSuggestedQuestionsProps): React.ReactNode {
  const userSession = useSession();
  const { setStoreState, chat, alchemyst } = useAlchemystStoreForAi(
    (store) => ({
      setStoreState: store.setStoreState,
      chat: store.chat,
      alchemyst: store.alchemyst,
    })
  );
  const handleSend = async (q: Question): Promise<void> => {
    setStoreState({
      chat: {
        ...chat,
        tools: { ...chat.tools, ...q.tools },
      },
    });
    await onSend(q.question, q.tools);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 2,
        padding: 2,
        height: '100%',
      }}
    >
      <Avatar
        src={alchemyst.avatarUrl}
        alt={alchemyst.persona}
        sx={{
          width: 100,
          height: 100,
          borderRadius: '50%',
          marginBottom: 2,
          cursor: 'pointer',
        }}
        onClick={() => {
          setStoreState({
            isCommandBarOpen: true,
          });
        }}
      />
      <Typography
        variant="h2"
        sx={{ fontFamily: 'Merriweather, cursive' }}
      >{`${getWish()}, ${userSession.data?.given_name}`}</Typography>
      <Typography
        variant="h5"
        sx={{ color: 'text.secondary', fontFamily: 'Merriweather, cursive' }}
      >
        {"What's on your mind?"}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          width: '80%',
          gap: 2,
          padding: 2,
        }}
      >
        {(questions[alchemyst.persona] ?? []).map((q) => (
          <Box
            key={q.id}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              padding: 2,
              borderRadius: 4,
              backgroundColor: 'rgba(0,0,0,0.1)',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.2)',
              },
            }}
            onClick={() => handleSend(q)}
          >
            <Typography>{q.icon}</Typography>
            <Typography
              sx={{
                fontFamily: 'Merriweather',
              }}
              component={'div'}
            >
              <MarkdownRender>{q.question}</MarkdownRender>
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default InitialSuggestedQuestions;
